import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import { registry, fieldRegistry } from '../common/registry';
import { registerCoreBlocks } from './blocks';
import { registerCoreFields } from './fields';
import { installSpaLinks } from './util/spaLinks';
import TagTemplateHero, { tagTemplateState } from './components/TagTemplateHero';
export { registry };
app.initializers.add('ernestdefoe-page-builder', () => {
  // Register core block + field components, and expose the registries so
  // third-party extensions can add their own.
  registerCoreBlocks();
  registerCoreFields();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  app.pageBuilder = registry;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  app.pageBuilderFields = fieldRegistry;

  // Map server-registered route names to their components (Mithril :param syntax).
  // Every page is its own chunk, fetched when its route opens: the editors are
  // staff-only and the public pages are only needed on their own routes, so
  // none of them belongs in the forum.js every visitor downloads.
  // The static "new"/"edit" routes are registered before the catch-all record
  // route so they take precedence.
  app.routes['pagebuilder.manage'] = {
    path: '/pagebuilder',
    component: () => import(/* webpackChunkName: 'forum/components/PageManagerPage', webpackMode: 'lazy-once' */ './components/PageManagerPage')
  };
  app.routes['pagebuilder.editor'] = {
    path: '/pagebuilder/editor',
    component: () => import(/* webpackChunkName: 'forum/components/BuilderEditorPage', webpackMode: 'lazy-once' */ './components/BuilderEditorPage')
  };
  app.routes['pagebuilder.sections'] = {
    path: '/pagebuilder/sections',
    component: () => import(/* webpackChunkName: 'forum/components/SectionEditorPage', webpackMode: 'lazy-once' */ './components/SectionEditorPage')
  };
  app.routes['pagebuilder.home'] = {
    path: '/home',
    component: () => import(/* webpackChunkName: 'forum/components/HomePage', webpackMode: 'lazy-once' */ './components/HomePage')
  };
  app.routes['pagebuilder.page'] = {
    path: '/page/:slug',
    component: () => import(/* webpackChunkName: 'forum/components/PageBuilderPage', webpackMode: 'lazy-once' */ './components/PageBuilderPage')
  };
  app.routes['pagebuilder.content.list'] = {
    path: '/c/:type',
    component: () => import(/* webpackChunkName: 'forum/components/RecordListPage', webpackMode: 'lazy-once' */ './components/RecordListPage')
  };
  app.routes['pagebuilder.content.new'] = {
    path: '/c/:type/new',
    component: () => import(/* webpackChunkName: 'forum/components/RecordEditorPage', webpackMode: 'lazy-once' */ './components/RecordEditorPage')
  };
  app.routes['pagebuilder.content.edit'] = {
    path: '/c/:type/:slug/edit',
    component: () => import(/* webpackChunkName: 'forum/components/RecordEditorPage', webpackMode: 'lazy-once' */ './components/RecordEditorPage')
  };
  app.routes['pagebuilder.content.record'] = {
    path: '/c/:type/:slug',
    component: () => import(/* webpackChunkName: 'forum/components/RecordPage', webpackMode: 'lazy-once' */ './components/RecordPage')
  };

  // Add a nav link per viewable content type to the forum's index nav.
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    const types = app.forum.attribute('pagebuilderContentTypes') || [];
    types.forEach((t, i) => {
      items.add(`pagebuilder-${t.slug}`, LinkButton.component({
        href: `/c/${t.slug}`,
        icon: t.icon || 'fas fa-database'
      }, t.name_plural), 50 - i);
    });
  });

  // Admins get a live "Pages" entry — the whole page lifecycle is managed on
  // the forum side (full-width), never the cramped admin panel.
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    if (!isAdmin()) return;
    items.add('pagebuilder-manage', LinkButton.component({
      href: '/pagebuilder',
      icon: 'fas fa-pen-ruler'
    }, app.translator.trans('ernestdefoe-page-builder.forum.manager.title')), -10);
  });

  // Add "Promote to article" to a discussion's controls dropdown (admins only,
  // and only when an "articles" content type exists to promote into).
  extend(DiscussionControls, 'moderationControls', function (items, discussion) {
    if (!isAdmin()) return;
    const types = app.forum.attribute('pagebuilderContentTypes') || [];
    if (!types.some(t => t.slug === 'articles')) return;

    // Toggle: a promoted discussion shows "Demote to discussion"; otherwise
    // "Promote to article".
    if (discussion && discussion.attribute && discussion.attribute('pagebuilderRecordUrl')) {
      items.add('pagebuilder-demote', Button.component({
        icon: 'fas fa-rotate-left',
        onclick: () => demoteToDiscussion(discussion)
      }, app.translator.trans('ernestdefoe-page-builder.forum.promote.demote_button')));
    } else {
      items.add('pagebuilder-promote', Button.component({
        icon: 'fas fa-newspaper',
        onclick: () => promoteToArticle(discussion)
      }, app.translator.trans('ernestdefoe-page-builder.forum.promote.button')));
    }
  });

  // Section and page links navigate in place instead of reloading the forum.
  // Not in the editors, where a click selects instead of following.
  installSpaLinks('.PageBuilderPage:not(.PageBuilderPage--editing), .PB-RecordList, .PB-Record, .PB-TagTemplate');

  // Links to a promoted discussion go straight to its article. The discussion
  // page used to draw first and then redirect, so a visitor saw the forum's
  // discussion styling flash before the article (Subarist, 2026-10-16). A link
  // to a particular post (`near`) still opens the thread.
  const discussionRoute = app.route.discussion;
  app.route.discussion = function (discussion, near) {
    const url = !near && !isAdmin() && discussion && discussion.attribute && discussion.attribute('pagebuilderRecordUrl');
    return url ? app.forum.attribute('baseUrl') + url : discussionRoute.call(this, discussion, near);
  };

  // A link that was built without the discussion (a widget's plain /d/123):
  // if the store already knows it is promoted, redirect before the page draws.
  // The route change lands after the first render, so the page draws nothing
  // meanwhile instead of the discussion it is leaving.
  extend(DiscussionPage.prototype, 'oninit', function () {
    if (isAdmin()) return;
    const id = String(m.route.param('id') || '').split('-')[0];
    const known = id && app.store.getById('discussions', id);
    const url = known && known.attribute && known.attribute('pagebuilderRecordUrl');
    if (url) {
      this.pbRedirecting = true;
      m.route.set(url, null, {
        replace: true
      });
    }
  });
  override(DiscussionPage.prototype, 'view', function (original) {
    return this.pbRedirecting ? m('div.DiscussionPage') : original();
  });

  // A promoted discussion is an article's comment thread — send regular visitors
  // straight to the article (the canonical page). Admins are NOT redirected, so
  // they can still open the discussion to manage or demote it.
  extend(DiscussionPage.prototype, 'show', function (_, discussion) {
    const url = discussion && discussion.attribute && discussion.attribute('pagebuilderRecordUrl');
    if (url && !isAdmin()) {
      m.route.set(url);
    }
  });
});

// The tag page template replaces Flarum's tag banner. Registered at a lower
// priority so it runs after flarum/tags: tags' own override returns its banner
// without calling the next one, so ours must be the outer wrapper.
app.initializers.add('ernestdefoe-page-builder-tag-template', () => {
  override(IndexPage.prototype, 'hero', function (original) {
    const tag = app.currentTag?.();
    if (!tag) return original();
    const state = tagTemplateState();
    if (state === 'ready') return m(TagTemplateHero, {
      model: tag
    });
    // Loading: nothing yet, rather than Flarum's banner flashing first.
    return state === 'loading' ? null : original();
  });
}, -10);

/** Forum-side admin check (admin group id is 1 in Flarum). */
function isAdmin() {
  const u = app.session.user;
  if (!u) return false;
  try {
    return (u.groups() || []).some(g => g && String(g.id()) === '1');
  } catch {
    return false;
  }
}
function promoteToArticle(discussion) {
  if (!confirm(app.translator.trans('ernestdefoe-page-builder.forum.promote.confirm'))) {
    return;
  }
  app.request({
    method: 'POST',
    url: `${app.forum.attribute('apiUrl')}/pagebuilder/promote`,
    body: {
      discussion_id: Number(discussion.id())
    }
  }).then(res => {
    app.alerts.show({
      type: 'success'
    }, app.translator.trans('ernestdefoe-page-builder.forum.promote.success'));
    m.route.set(`/c/articles/${res.data.slug}`);
  }).catch(() => {
    app.alerts.show({
      type: 'error'
    }, app.translator.trans('ernestdefoe-page-builder.forum.promote.error'));
  });
}
function demoteToDiscussion(discussion) {
  if (!confirm(app.translator.trans('ernestdefoe-page-builder.forum.promote.demote_confirm'))) {
    return;
  }
  app.request({
    method: 'POST',
    url: `${app.forum.attribute('apiUrl')}/pagebuilder/demote`,
    body: {
      discussion_id: Number(discussion.id())
    }
  }).then(() => {
    app.alerts.show({
      type: 'success'
    }, app.translator.trans('ernestdefoe-page-builder.forum.promote.demote_success'));
    // Full reload so the now-normal discussion is shown with fresh data
    // (and no stale redirect attribute).
    window.location.href = `${app.forum.attribute('baseUrl')}/d/${discussion.id()}`;
  }).catch(() => {
    app.alerts.show({
      type: 'error'
    }, app.translator.trans('ernestdefoe-page-builder.forum.promote.demote_error'));
  });
}flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/PageManagerPage', '547', 'ernestdefoe-page-builder', 'forum/components/PageManagerPage');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/BuilderEditorPage', '476', 'ernestdefoe-page-builder', 'forum/components/BuilderEditorPage');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/SectionEditorPage', '515', 'ernestdefoe-page-builder', 'forum/components/SectionEditorPage');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/HomePage', '10', 'ernestdefoe-page-builder', 'forum/components/HomePage');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/PageBuilderPage', '556', 'ernestdefoe-page-builder', 'forum/components/PageBuilderPage');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/RecordListPage', '999', 'ernestdefoe-page-builder', 'forum/components/RecordListPage');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/RecordEditorPage', '743', 'ernestdefoe-page-builder', 'forum/components/RecordEditorPage');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/RecordPage', '943', 'ernestdefoe-page-builder', 'forum/components/RecordPage');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/BuilderEditorPage', '476', 'ernestdefoe-page-builder', 'forum/loadBuilder');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/SectionEditorPage', '515', 'ernestdefoe-page-builder', 'common/sections');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/SectionEditorPage', '515', 'ernestdefoe-page-builder', 'forum/components/ProductInspector');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/HomePage', '10', 'ernestdefoe-page-builder', 'forum/components/LayoutRenderer');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/HomePage', '10', 'ernestdefoe-page-builder', 'forum/components/LiveEditor');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/HomePage', '10', 'ernestdefoe-page-builder', 'common/dnd');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/HomePage', '10', 'ernestdefoe-page-builder', 'admin/components/SchemaForm');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/HomePage', '10', 'ernestdefoe-page-builder', 'admin/components/BlockStyleForm');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/HomePage', '10', 'ernestdefoe-page-builder', 'forum/hydrateWidgets');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/HomePage', '10', 'ernestdefoe-page-builder', 'forum/waymark');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/RecordListPage', '999', 'ernestdefoe-page-builder', 'forum/components/HeroForm');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/RecordListPage', '999', 'ernestdefoe-page-builder', 'forum/components/ListingHeroModal');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/RecordEditorPage', '743', 'ernestdefoe-page-builder', 'forum/recordHero');
flarum.reg.addChunkModule('ernestdefoe-page-builder:forum/components/RecordPage', '943', 'ernestdefoe-page-builder', 'forum/components/RecordComments');